<template>
	<div class="add-counterparty-modal">
		<app-loader
			v-if="isLoading || isSaving"
			:style="loaderStyle"
		/>

		<!-- Поиск существующего КА -->
		<template v-if="mode === 'search'">
			<div class="add-counterparty-modal__search">
				<app-input
					ref="$input"
					v-model="filter"
					class="add-counterparty-modal__search-input"
					:placeholder="$t('counterparty_name_or_inn')"
				/>
				<div
					v-if="props.showCloseIcon"
					class="add-counterparty-modal__close"
				>
					<app-tooltip :text="$t('close_label')">
						<template #activator>
							<app-icon
								name="close"
								width="20px"
								color="#D2D2D2"
								hover-color="#499259"
								cursor="pointer"
								@click="emit('close')"
							/>
						</template>
					</app-tooltip>
				</div>
			</div>

			<!-- 1. Ничего не введено -->
			<div
				v-if="!trimmedFilter"
				class="add-counterparty-modal__hint"
			>
				{{ $t('counterparty_search_hint') }}
			</div>

			<!-- Поиск завершён -->
			<template v-else-if="!isLoading">
				<!-- 2. Ничего не найдено -->
				<div
					v-if="!sortedCounterparties.length"
					class="add-counterparty-modal__hint"
				>
					<div>{{ $t('counterparty_not_found') }}</div>
					<div
						v-if="isFilterInn"
						class="add-counterparty-modal__hint-create"
						@click="openCreateForm"
					>
						{{ $t('counterparty_create_with_inn', { inn: trimmedFilter }) }}
					</div>
					<div v-else>
						{{ $t('counterparty_create_hint') }}
					</div>
				</div>

				<!-- 3. Найдены КА -->
				<template v-else>
					<div class="add-counterparty-modal__found">
						{{ $t('search_found_count', { count: sortedCounterparties.length }) }}
					</div>
					<div
						ref="$body"
						class="add-counterparty-modal__body"
					>
						<div
							v-for="cp in sortedCounterparties"
							:key="cp.Id"
							:class="{
								'add-counterparty-modal__body-item': true,
								'add-counterparty-modal__body-item--disabled': isRowDisabled(cp)
							}"
						>
							<app-checkbox
								:style="{ height: '16px', marginRight: '8px' }"
								:value="addedIds.has(cp.Id)"
								:disabled="isRowDisabled(cp)"
								text=""
								@input="onToggle($event, cp)"
							/>
							<app-tooltip
								v-if="isRowDisabled(cp)"
								:text="$t('counterparty_attached_to_other')"
								:disabled="infoHoverId === cp.Id"
							>
								<template #activator>
									<div class="add-counterparty-modal__body-item-text">
										<span v-html="counterpartyLabelHtml(cp)" /><span class="add-counterparty-modal__body-item-info">&nbsp;<app-tooltip
											class="add-counterparty-modal__body-item-info-icon"
											:text="$t('info')"
											@mouseenter="infoHoverId = cp.Id"
											@mouseleave="infoHoverId = null"
										>
											<template #activator>
												<app-icon
													name="info-circle-outline"
													width="14px"
													color="#979797"
													hover-color="#499259"
													cursor="pointer"
													@click.stop="emit('open-info', cp.Id)"
												/>
											</template>
										</app-tooltip></span>
									</div>
								</template>
							</app-tooltip>
							<div
								v-else
								class="add-counterparty-modal__body-item-text"
								@click="onToggleFromName(cp)"
							>
								<span v-html="counterpartyLabelHtml(cp)" /><span class="add-counterparty-modal__body-item-info">&nbsp;<app-tooltip
									class="add-counterparty-modal__body-item-info-icon"
									:text="$t('info')"
								>
									<template #activator>
										<app-icon
											name="info-circle-outline"
											width="14px"
											color="#979797"
											hover-color="#499259"
											cursor="pointer"
											@click.stop="emit('open-info', cp.Id)"
										/>
									</template>
								</app-tooltip></span>
							</div>
						</div>
					</div>
				</template>
			</template>
		</template>

		<!-- Создание нового КА -->
		<template v-else>
			<div class="add-counterparty-modal__search">
				<div class="add-counterparty-modal__title">
					{{ $t('counterparty_new') }}
				</div>
				<div
					v-if="props.showCloseIcon"
					class="add-counterparty-modal__close"
				>
					<app-tooltip :text="$t('close_label')">
						<template #activator>
							<app-icon
								name="close"
								width="20px"
								color="#D2D2D2"
								hover-color="#499259"
								cursor="pointer"
								@click="emit('close')"
							/>
						</template>
					</app-tooltip>
				</div>
			</div>

			<div class="add-counterparty-modal__form">
				<app-input
					v-model="form.Name"
					:label="$t('name_upcase')"
					required
				/>
				<app-input
					v-model="form.TaxpayerNumber"
					:label="$t('inn')"
					required
					:validation-regex="INN_REGEX"
					show-validation-icon
					:error="innTaken"
					:error-hint-text="innErrorHint"
					:validation-hint-text="$t('counterparty_inn_valid')"
				/>
				<app-input
					v-model="form.ReasonCode"
					:label="$t('kpp')"
				/>
				<app-input
					v-model="form.PersonalAccount"
					:label="$t('personal_account2')"
				/>
				<app-input
					v-model="form.BankAccount"
					:label="$t('settlement_account')"
				/>
				<app-input
					v-model="form.BankName"
					:label="$t('bank_name')"
				/>
				<app-input
					v-model="form.BankIdCode"
					:label="$t('bik')"
				/>
				<app-input
					v-model="form.CorrespondentBankAccount"
					:label="$t('correspondent_account')"
				/>
				<app-input
					v-model="form.LegalAddress"
					:label="$t('legal_address')"
				/>
				<app-input
					v-model="form.LegalForm"
					:label="$t('legal_form')"
				/>
				<app-input
					v-model="form.Note"
					:label="$t('comment')"
				/>
				<app-select
					:items="instances"
					:model-value="form.InstanceName1C"
					:disabled="!instances.length"
					:placeholder="$t(instances.length ? 'one_c_instance' : 'counterparty_1c_unavailable')"
					floating-label
					required
					searchable
					@update:model-value="onInstanceChange"
				/>

				<div
					v-if="errorText"
					class="add-counterparty-modal__error"
				>
					{{ errorText }}
				</div>
			</div>

			<div class="add-counterparty-modal__actions">
				<button
					type="button"
					class="add-counterparty-modal__actions-button add-counterparty-modal__back"
					@click="backToSearch"
				>
					<app-icon
						name="chevron-left"
						width="16px"
						color="#979797"
					/>
					{{ $t('back') }}
				</button>
				<app-button
					class="add-counterparty-modal__actions-button"
					:label="$t('counterparty_attach')"
					:disabled="!canSubmit"
					@click="onSubmit"
				/>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	reactive,
	ref,
	watch
} from "vue";
import { i18n } from "@/i18n";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppSelect, { SelectItem, SelectModelValue } from "@/components/ui-kit/select/AppSelect.vue";
import AppButton from "@/components/ui-kit/button/AppButton.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { clientPageStore } from "@/store/client-page";
import { commonRequestCreds, financesStore } from "@/store/finances";
import {
	CounterpartyAddContainer,
	CounterpartySearched
} from "@/models/interfaces/finances/counterparty-models";
import { debounce } from "lodash";
import { escapeHtml, highlightSearchText } from "@/utils/textHelpers";

const props = defineProps<{
	clientId: number,
	addedCounterparties: { Id: number, Name: string }[],
	showCloseIcon?: boolean,
}>()

const emit = defineEmits<{
	(e: 'close'): void,
	(e: 'mode-change', mode: 'search' | 'create'): void,
	(e: 'open-info', counterpartyId: number): void,
}>()

// ИНН — 10 или 12 цифр
const INN_REGEX = /^(\d{10}|\d{12})$/

const store = clientPageStore()
const t = (key: string, named?: Record<string, unknown>): string => String(named ? i18n.global.t(key, named) : i18n.global.t(key))

const finances = financesStore()

const $input = ref<typeof AppInput | null>(null)
const $body = ref<HTMLDivElement | null>(null)

const mode = ref<'search' | 'create'>('search')
const isLoading = ref(false)
const isSaving = ref(false)
const errorText = ref('')

const filter = ref('')
const appliedFilter = ref('')
const counterparties = ref<CounterpartySearched[]>([])

const detachedHere = ref(new Set<number>())

const infoHoverId = ref<number | null>(null)

const instances = ref<SelectItem[]>([])
const innTaken = ref(false)

const emptyForm = (): CounterpartyAddContainer => ({
	Name: '',
	TaxpayerNumber: '',
	ReasonCode: '',
	PersonalAccount: '',
	BankAccount: '',
	BankName: '',
	BankIdCode: '',
	CorrespondentBankAccount: '',
	LegalAddress: '',
	LegalForm: '',
	Note: '',
	CounterTaxpayerNumber: [10, 12],
	InstanceName1C: null,
})

const form = reactive<CounterpartyAddContainer>(emptyForm())

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const addedIds = computed(() => new Set((props.addedCounterparties || []).map(c => c.Id)))

const sortedCounterparties = computed<CounterpartySearched[]>(() => {
	const list = counterparties.value || []
	const added = list.filter(c => addedIds.value.has(c.Id))
	const rest = list.filter(c => !addedIds.value.has(c.Id))
	return [...added, ...rest]
})

const counterpartyLabel = (cp: CounterpartySearched) => {
	const parts = [cp.Name?.trim() ?? '']
	if (cp.TaxpayerNumber) {
		parts.push(t('counterparty_inn_value', { inn: cp.TaxpayerNumber.trim() }))
	}
	if (cp.Instance1C) {
		parts.push(`[${cp.Instance1C.trim()}]`)
	}
	return parts.join(' · ')
}

const counterpartyLabelHtml = (cp: CounterpartySearched) => {
	return highlightSearchText(appliedFilter.value, escapeHtml(counterpartyLabel(cp)))
}

// Привязан к чужому клиенту => взять его нельзя.
const isRowDisabled = (cp: CounterpartySearched) =>
	cp.AttachedToClient && !addedIds.value.has(cp.Id) && !detachedHere.value.has(cp.Id)

const trimmedFilter = computed(() => filter.value.trim())

// Введённая строка — валидный ИНН (10 или 12 цифр).
const isFilterInn = computed(() => INN_REGEX.test(trimmedFilter.value))

const isInnValid = computed(() => INN_REGEX.test(form.TaxpayerNumber.trim()))

const innErrorHint = computed(() => innTaken.value
	? t('counterparty_inn_in_use')
	: t('counterparty_inn_invalid_length'))

const canSubmit = computed(() =>
	isInnValid.value && !!form.Name.trim() && form.InstanceName1C != null && !innTaken.value && !isSaving.value)

const onFetch = async () => {
	try {
		isLoading.value = true
		const requestedFilter = filter.value
		const result = await finances.counterpartyGetFilteredList({
			Filter: requestedFilter,
			ForBills: false,
		})
		if (result) {
			appliedFilter.value = requestedFilter
		}
		counterparties.value = result?.List ?? []
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onSearch = async () => {
	$body.value?.scrollTo({ top: 0 })
	await onFetch()
}

const debouncedSearch = debounce(onSearch, 500)

const onAttach = async (cp: CounterpartySearched) => {
	try {
		isLoading.value = true
		await store.attachCounterparty({ Id: cp.Id, IsDraft: cp.IsDraft })
		detachedHere.value.delete(cp.Id)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onDetach = async (cp: CounterpartySearched) => {
	try {
		isLoading.value = true
		await store.detachCounterparty(cp.Id)
		detachedHere.value.add(cp.Id)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onToggle = async (checked: boolean, cp: CounterpartySearched) => {
	if (checked) {
		await onAttach(cp)
	} else {
		await onDetach(cp)
	}
}

const onToggleFromName = async (cp: CounterpartySearched) => {
	await onToggle(!addedIds.value.has(cp.Id), cp)
}

const onInstanceChange = (value: SelectModelValue) => {
	form.InstanceName1C = value == null ? null : (value as string)
}

// Авто-выбор «По умолчанию» за пользователя (опция с id === '').
const applyDefaultInstance = () => {
	if (form.InstanceName1C != null) {
		return
	}
	if (instances.value.some(i => i.id === '')) {
		form.InstanceName1C = ''
	}
}

const resetForm = () => {
	Object.assign(form, emptyForm())
	innTaken.value = false
	errorText.value = ''
}

const openCreateForm = () => {
	resetForm()
	form.TaxpayerNumber = trimmedFilter.value
	mode.value = 'create'
	applyDefaultInstance()
}

const backToSearch = () => {
	resetForm()
	mode.value = 'search'
}

const checkInn = debounce(async () => {
	if (!isInnValid.value) {
		innTaken.value = false
		return
	}
	try {
		const { response_ok } = await finances.checkNewTaxpayerNumber({
			Value: form.TaxpayerNumber.trim(),
			...commonRequestCreds(),
		})
		innTaken.value = !response_ok
	} catch (e) {
		console.error(e)
	}
}, 500)

const onSubmit = async () => {
	if (!canSubmit.value) return
	try {
		isSaving.value = true
		errorText.value = ''
		await store.createCounterparty({
			...form,
			Name: form.Name.trim(),
			TaxpayerNumber: form.TaxpayerNumber.trim(),
		})
		emit('close')
	} catch (e) {
		errorText.value = t('counterparty_create_error')
		console.error(e)
	} finally {
		isSaving.value = false
	}
}

const loadInstances = async () => {
	try {
		const result: any = await finances.getSynchronizationInstances()
		// Эндпоинт отдаёт голый массив имён экземпляров, где null — «по умолчанию».
		const rawList: (string | null)[] = Array.isArray(result) ? result : (result?.List ?? [])
		instances.value = rawList.map((name) => ({
			id: name ?? '',
			label: name || t('by_default'),
		}))

		if (mode.value === 'create') {
			applyDefaultInstance()
		}
	} catch (e) {
		console.error(e)
	}
}

watch(filter, (newVal, oldVal) => {
	if (newVal === oldVal) {
		return
	}

	if (!newVal.trim()) {
		debouncedSearch.cancel()
		isLoading.value = false
		counterparties.value = []
		return
	}

	// Оверлей включаем сразу, чтобы во время дебаунса не мигало «КА не найден».
	isLoading.value = true
	debouncedSearch()
})

watch(() => form.TaxpayerNumber, () => {
	innTaken.value = false
	checkInn()
})

watch(mode, (value) => emit('mode-change', value))

onMounted(async () => {
	try {
		// Список грузим только при вводе — при пустом поле показываем подсказку.
		await loadInstances()
		await nextTick(() => $input.value?.focusInput())
	} catch (e) {
		console.error(e)
	}
})
</script>

<style scoped lang="scss">
.add-counterparty-modal {
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	padding: 12px 0 0 12px;

	&__search {
		display: flex;
		align-items: center;
		gap: 8px;
		width: 100%;
		padding-right: 12px;

		&-input {
			width: 100%;
		}
	}

	&__title {
		flex: 1;
		font-size: 14px;
		line-height: 18px;
		font-weight: 600;
		color: #4D4D4D;
	}

	&__hint {
		margin-top: 4px;
		padding: 8px 20px;
		text-align: center;
		font-size: 14px;
		line-height: 18px;
		color: #979797;

		&-create {
			color: #438451;
			cursor: pointer;

			&:hover {
				text-decoration: underline;
			}
		}
	}

	&__found {
		margin-top: 4px;
		padding-right: 12px;
		font-size: 14px;
		line-height: 18px;
		color: #979797;
	}

	&__body {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 8px;
		margin-top: 4px;
		overflow: auto;
		padding-right: 12px;
		padding-bottom: 12px;

		&-item {
			display: flex;
			font-size: 14px;
			line-height: 18px;
			color: #333;
			cursor: pointer;
			transition: color 0.1s;

			&-text {
				font-size: 14px;
				line-height: 18px;
			}

			&-info {
				white-space: nowrap;

				&-icon {
					vertical-align: middle;
				}
			}

			&:hover:not(&--disabled) {
				text-decoration: underline;
				color: #396742;
			}

			&--disabled {
				color: #979797;
				cursor: default;
			}
		}
	}

	&__form {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 12px;
		min-height: 0;
		overflow: auto;
		padding: 10px 12px 12px 0;
	}

	&__error {
		font-size: 13px;
		line-height: 18px;
		color: #BA4C4C;
	}

	&__actions {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		padding: 8px 12px 12px 0;

		&-button {
			flex: 1;
		}
	}

	&__back {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		height: 32px;
		padding: 10px 16px;
		border: none;
		border-radius: 8px;
		background: #FFF;
		color: #979797;
		font-family: 'Roboto';
		font-size: 14px;
		line-height: 18px;
		font-weight: 600;
		cursor: pointer;

		&:hover {
			background: #F2F2F2;
		}
	}
}
</style>
